import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  FileSearch,
  Gauge,
  Landmark,
  Loader2,
  Scale,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import styled from "styled-components";

import Sidebar from "../Sidebar";
import TopBar from "../TopBar";
import { Content, ImportWrapper, PageBody } from "../import/ImportComponentStyles";
import {
  METHODOLOGY_VERSION,
  POSITION_DEFINITIONS,
  buildFinancialSeries,
  buildQualityChecks,
  calculateAllMetrics,
  getPosition,
} from "./financialAnalysisEngine";

const API_BASE = "https://server229256.nazwa.pl";

const AnalysisBody = styled(PageBody)`
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: #f5f5f5;
  color: #2b2b2b;
  font-family: Arial, sans-serif;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Report = styled.div`
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
`;

const Hero = styled.header`
  overflow: hidden;
  margin-bottom: 20px;
  border: 1px solid #ccc;
  border-top: 4px solid #87ceeb;
  border-radius: 8px;
  background: #f9f9f9;
  color: #2b2b2b;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const HeroTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(320px, 1fr);
  align-items: start;
  gap: 32px;
  padding: 20px;

  h1 {
    margin: 6px 0 8px;
    color: #333;
    font-size: clamp(22px, 2vw, 30px);
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  p {
    max-width: 720px;
    margin: 0;
    color: #666;
    font-size: 14px;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
`;

const HeroIdentity = styled.div`
  min-width: 0;
  text-align: left;
`;

const Eyebrow = styled.div`
  color: #69b9da;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const HeroMeta = styled.div`
  width: 100%;
  max-width: 470px;
  min-width: 250px;
  justify-self: end;
  align-self: start;
  color: #666;
  font-size: 13px;
  line-height: 1.8;
  text-align: right;

  strong {
    color: #333;
  }

  select {
    max-width: 280px;
    margin-top: 5px;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background: #fff;
    color: #333;
    font-size: 14px;
  }

  option {
    color: #333;
  }

  @media (max-width: 900px) {
    max-width: none;
    min-width: 0;
    justify-self: stretch;
    text-align: left;
  }
`;

const HeroKpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(165px, 1fr));
  border-top: 1px solid #ddd;
  background: #fff;
`;

const HeroKpi = styled.div`
  min-height: 92px;
  padding: 16px 18px;
  border-right: 1px solid #ddd;

  &:last-child {
    border-right: 0;
  }

  span {
    display: block;
    color: #666;
    font-size: 11px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin: 5px 0 3px;
    color: #333;
    font-family: Arial, sans-serif;
    font-size: 22px;
    font-weight: 700;
  }

  small {
    color: ${(props) => (props.$negative ? "#c62828" : "#2e7d32")};
    font-weight: 600;
  }
`;

const Panel = styled.section`
  margin-bottom: 20px;
  padding: 20px 20px 28px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f9f9f9;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  h2 {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 12px;
    padding: 10px 20px 12px;
    border: 1px solid rgba(135, 206, 235, 0.45);
    border-radius: 8px;
    background: linear-gradient(
      to right,
      rgba(135, 206, 235, 0.16),
      rgba(135, 206, 235, 0.04)
    );
    color: #2b2b2b;
    font-family: Arial, sans-serif;
    font-size: 1.05rem;
    font-weight: 700;

    svg {
      color: #69b9da;
    }
  }

  h3 {
    margin: 24px 0 10px;
    color: #666;
    font-family: Arial, sans-serif;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
  }
`;

const Lead = styled.p`
  max-width: none;
  margin: 10px 4px 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.55;
`;

const TwoColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ChartBox = styled.div`
  min-width: 0;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
  justify-content: center;
  color: #666;
  font-size: 12px;

  i {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 5px;
    border-radius: 50%;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(205px, 1fr));
  gap: 12px;
`;

const MetricCard = styled.article`
  padding: 16px;
  border: 1px solid #ccc;
  border-left: 4px solid
    ${(props) =>
      props.$tone === "good"
        ? "#2e7d32"
        : props.$tone === "bad"
        ? "#c62828"
        : props.$tone === "warning"
        ? "#ef9a21"
        : "#87ceeb"};
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);

  span {
    display: block;
    color: #666;
    font-size: 11px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin: 4px 0;
    color: #333;
    font-family: Arial, sans-serif;
    font-size: 21px;
  }

  small {
    color: #666;
    line-height: 1.35;
  }
`;

const TableScroll = styled.div`
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #fff;

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.7rem;
  }

  th,
  td {
    padding: 12px 9px;
    border-right: 1px solid #ddd;
    border-bottom: 1px solid #ddd;
    text-align: right;
    white-space: nowrap;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  th:first-child,
  td:first-child {
    text-align: left;
    white-space: normal;
  }

  th {
    background: #87ceeb;
    color: #333;
    font-size: 0.7rem;
    font-weight: 700;
  }

  tbody tr:nth-child(even) {
    background: #f9f9f9;
  }

  tbody tr:hover {
    background: #eef6ff;
  }

  tr.total td {
    font-weight: 800;
    background: rgba(135, 206, 235, 0.16);
  }

  td.negative {
    color: #c62828;
    font-weight: 600;
  }
`;

const CheckList = styled.div`
  display: grid;
  gap: 9px;
`;

const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 13px;
  border: 1px solid ${(props) => (props.$ok ? "#b7d8ba" : "#efb9b5")};
  border-radius: 8px;
  background: ${(props) => (props.$ok ? "#e7f9ed" : "#ffeaea")};
  color: ${(props) => (props.$ok ? "#2e7d32" : "#c62828")};

  small {
    display: block;
    color: #666;
  }
`;

const EmptyState = styled.div`
  padding: 70px 25px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f9f9f9;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
  color: #666;

  a,
  button {
    display: inline-block;
    margin-top: 14px;
    padding: 10px 15px;
    border: 0;
    border-radius: 4px;
    background: #2e7d32;
    color: #fff;
    text-decoration: none;
    font-weight: 700;
    cursor: pointer;
  }

  a:hover,
  button:hover {
    background: #155e36;
  }
`;

const FINANCIAL_SECTION_KEYS = new Set([
  "overview",
  "statement",
  "profitability",
  "liquidity",
  "leverage",
  "efficiency",
  "quality",
]);

const COLORS = ["#2e7d32", "#87ceeb", "#1565c0", "#c62828"];

function formatMoney(value, digits = 0) {
  if (!Number.isFinite(value)) return "n/d";
  return `${value.toLocaleString("pl-PL", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} zł`;
}

function formatMetric(metric) {
  if (!metric || !Number.isFinite(metric.value)) return "n/d";
  if (metric.unit === "%") {
    return metric.value.toLocaleString("pl-PL", {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }
  if (metric.unit === "PLN") return formatMoney(metric.value);
  if (metric.unit === "days") return `${Math.round(metric.value)} dni`;
  return `${metric.value.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}x`;
}

function relativeChange(current, previous) {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

function formatChange(value) {
  if (!Number.isFinite(value)) return "brak porównania";
  return `${value > 0 ? "+" : ""}${value.toLocaleString("pl-PL", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} r/r`;
}

function LineChart({ years, series, percent = false }) {
  const width = 720;
  const height = 270;
  const pad = { left: 58, right: 18, top: 22, bottom: 38 };
  const values = series.flatMap((item) => item.values).filter(Number.isFinite);
  if (!values.length) return <div style={{ padding: 35, color: "#666" }}>Brak danych do wykresu.</div>;
  let min = Math.min(0, ...values);
  let max = Math.max(0, ...values);
  if (min === max) max = min + 1;
  const x = (index) =>
    pad.left + (years.length === 1 ? (width - pad.left - pad.right) / 2 : (index * (width - pad.left - pad.right)) / (years.length - 1));
  const y = (value) => pad.top + ((max - value) * (height - pad.top - pad.bottom)) / (max - min);
  const axisLabel = (value) =>
    percent
      ? value.toLocaleString("pl-PL", { style: "percent", maximumFractionDigits: 0 })
      : Math.abs(value) >= 1000
      ? `${(value / 1000).toLocaleString("pl-PL", { maximumFractionDigits: 0 })} tys.`
      : value.toLocaleString("pl-PL", { maximumFractionDigits: 0 });

  return (
    <>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", display: "block" }} role="img">
        {[0, 1, 2, 3, 4].map((step) => {
          const value = min + ((max - min) * step) / 4;
          return (
            <g key={step}>
              <line x1={pad.left} x2={width - pad.right} y1={y(value)} y2={y(value)} stroke="#ddd" strokeDasharray="3 3" />
              <text x={pad.left - 8} y={y(value) + 4} textAnchor="end" fontSize="10" fill="#666">{axisLabel(value)}</text>
            </g>
          );
        })}
        {min < 0 && max > 0 && <line x1={pad.left} x2={width - pad.right} y1={y(0)} y2={y(0)} stroke="#888" strokeDasharray="4 4" />}
        {years.map((year, index) => (
          <text key={year} x={x(index)} y={height - 12} textAnchor="middle" fontSize="11" fill="#666">{year}</text>
        ))}
        {series.map((item, seriesIndex) => {
          const color = item.color || COLORS[seriesIndex % COLORS.length];
          const points = item.values
            .map((value, index) => (Number.isFinite(value) ? `${x(index)},${y(value)}` : null))
            .filter(Boolean)
            .join(" ");
          return (
            <g key={item.name}>
              <polyline points={points} fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round" />
              {item.values.map((value, index) =>
                Number.isFinite(value) ? <circle key={index} cx={x(index)} cy={y(value)} r="4" fill="#fff" stroke={color} strokeWidth="2.5" /> : null
              )}
            </g>
          );
        })}
      </svg>
      <Legend>
        {series.map((item, index) => (
          <span key={item.name}><i style={{ background: item.color || COLORS[index % COLORS.length] }} />{item.name}</span>
        ))}
      </Legend>
    </>
  );
}

function MetricCards({ metrics }) {
  return (
    <Cards>
      {metrics.map((item) => (
        <MetricCard key={item.code} $tone={item.tone}>
          <span>{item.code}</span>
          <strong>{formatMetric(item)}</strong>
          <div>{item.label}</div>
          <small>{item.interpretation}</small>
        </MetricCard>
      ))}
    </Cards>
  );
}

function MetricHistory({ allMetrics, category }) {
  const rows = allMetrics[allMetrics.length - 1]?.[category] || [];
  return (
    <TableScroll style={{ marginTop: 20 }}>
      <table>
        <thead>
          <tr>
            <th>Wskaźnik</th>
            {allMetrics.map((year) => <th key={year.year}>{year.year}</th>)}
            <th>Interpretacja</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code}>
              <td><strong>{row.code}</strong> · {row.label}</td>
              {allMetrics.map((year) => {
                const value = year[category].find((metric) => metric.code === row.code);
                return <td key={year.year}>{formatMetric(value)}</td>;
              })}
              <td style={{ textAlign: "left", whiteSpace: "normal", minWidth: 260 }}>{row.interpretation}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function StatementTable({ years, group }) {
  const definitions = POSITION_DEFINITIONS.filter((definition) => definition.group === group).filter(
    (definition) => definition.level === 0 || years.some((year) => Math.abs(getPosition(year, definition.symbol)) > 0)
  );
  return (
    <TableScroll>
      <table>
        <thead><tr><th>Pozycja</th>{years.map((year) => <th key={year.year}>{year.year}</th>)}</tr></thead>
        <tbody>
          {definitions.map((definition) => (
            <tr key={definition.symbol} className={definition.level === 0 ? "total" : ""}>
              <td style={{ paddingLeft: 11 + definition.level * 16 }}>
                <strong>{definition.symbol}</strong> · {definition.label}
              </td>
              {years.map((year) => {
                const value = getPosition(year, definition.symbol);
                return <td key={year.year} className={value < 0 ? "negative" : ""}>{formatMoney(value, 2)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function narrative(latest, previous) {
  if (!latest) return "";
  if (!previous) return `Dostępny jest jeden okres danych: ${latest.year}. Dodaj kolejny plik, aby uruchomić analizę zmian.`;
  const revenue = relativeChange(latest.revenue, previous.revenue);
  const costs = relativeChange(latest.operatingCosts, previous.operatingCosts);
  const result = latest.netProfit >= 0 ? "zyskiem" : "stratą";
  return `W ${latest.year} roku przychody zmieniły się o ${formatChange(revenue).replace(" r/r", "")}, a koszty operacyjne o ${formatChange(costs).replace(" r/r", "")}. Rok zakończył się ${result} netto ${formatMoney(Math.abs(latest.netProfit))}.`;
}

export default function FinancialAnalysisPage() {
  const location = useLocation();
  const [list, setList] = useState([]);
  const [selectedNip, setSelectedNip] = useState("");
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const queryState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requestedSection = params.get("section");
    return {
      activeSection: FINANCIAL_SECTION_KEYS.has(requestedSection)
        ? requestedSection
        : "overview",
      refreshRequest: params.get("refresh") || "",
    };
  }, [location.search]);

  const { activeSection, refreshRequest } = queryState;

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE}/api/financial-statements`, {
        headers: { Authorization: localStorage.getItem("token") },
      });
      const rows = Array.isArray(response.data) ? response.data : [];
      setList(rows);
      const nips = [...new Set(rows.map((item) => item.metadata?.nip).filter(Boolean))];
      setSelectedNip((current) => (current && nips.includes(current) ? current : nips[0] || ""));
      if (!rows.length) setLoading(false);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList, refreshRequest]);

  useEffect(() => {
    if (!selectedNip) return;
    let cancelled = false;
    const loadDetails = async () => {
      setLoading(true);
      setError("");
      setDetails([]);
      try {
        const selected = list.filter((item) => item.metadata?.nip === selectedNip);
        const responses = await Promise.all(
          selected.map((item) =>
            axios.get(`${API_BASE}/api/financial-statements/${item._id}`, {
              headers: { Authorization: localStorage.getItem("token") },
            })
          )
        );
        if (!cancelled) setDetails(responses.map((response) => response.data));
      } catch (requestError) {
        if (!cancelled) setError(requestError?.response?.data?.error || requestError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDetails();
    return () => { cancelled = true; };
  }, [selectedNip, list]);

  const analysis = useMemo(() => {
    const series = buildFinancialSeries(details);
    const metrics = calculateAllMetrics(series.years);
    return { ...series, metrics, quality: buildQualityChecks(series.years, series.overlaps) };
  }, [details]);

  const latest = analysis.metrics[analysis.metrics.length - 1];
  const previous = analysis.metrics[analysis.metrics.length - 2];
  const latestDocument = [...details].sort((a, b) => String(b.metadata?.periodTo).localeCompare(String(a.metadata?.periodTo)))[0];
  const availableNips = [...new Set(list.map((item) => item.metadata?.nip).filter(Boolean))];
  const years = analysis.years.map((item) => item.year);

  const renderMetricSection = (category, title, icon, lead, chartSeries, percent = false) => {
    const Icon = icon;
    return (
      <Panel>
        <h2><Icon size={22} /> {title}</h2>
        <Lead>{lead}</Lead>
        <TwoColumns>
          <ChartBox>
            <LineChart years={years} series={chartSeries} percent={percent} />
          </ChartBox>
          <MetricCards metrics={(latest?.[category] || []).slice(0, 4)} />
        </TwoColumns>
        <MetricHistory allMetrics={analysis.metrics} category={category} />
      </Panel>
    );
  };

  return (
    <ImportWrapper>
      <Sidebar />
      <Content>
        <TopBar />
        <AnalysisBody>
          <Report>
            {loading && !details.length ? (
              <EmptyState><Loader2 className="spin" size={36} /><p>Ładowanie analizy finansowej…</p></EmptyState>
            ) : error ? (
              <EmptyState><AlertTriangle size={38} color="#c62828" /><p>{error}</p><button onClick={loadList}>Spróbuj ponownie</button></EmptyState>
            ) : !list.length ? (
              <EmptyState><FileSearch size={42} /><h2>Brak sprawozdań finansowych</h2><p>Najpierw zaimportuj plik XML.</p><a href="/import">Przejdź do importu</a></EmptyState>
            ) : (
              <>
                <Hero>
                  <HeroTop>
                    <HeroIdentity>
                      <Eyebrow>Analiza sprawozdania finansowego</Eyebrow>
                      <h1>{latestDocument?.metadata?.companyName || "Sprawozdanie finansowe"}</h1>
                      <p>{analysis.years.length} okresy danych · {analysis.metrics.flatMap((year) => year.allMetrics).filter((item) => Number.isFinite(item.value)).length} obliczonych wartości wskaźnikowych · metodyka {METHODOLOGY_VERSION}</p>
                    </HeroIdentity>
                    <HeroMeta>
                      NIP <strong>{selectedNip}</strong><br />
                      PKD <strong>{latestDocument?.metadata?.pkdCodes?.join(", ") || "–"}</strong><br />
                      Okresy <strong>{years.join(", ")}</strong><br />
                      {availableNips.length > 1 && (
                        <select value={selectedNip} onChange={(event) => setSelectedNip(event.target.value)}>
                          {availableNips.map((nip) => <option key={nip} value={nip}>{nip}</option>)}
                        </select>
                      )}
                    </HeroMeta>
                  </HeroTop>
                  <HeroKpis>
                    {[
                      ["Przychody", latest?.revenue, previous?.revenue],
                      ["EBITDA", latest?.ebitda, previous?.ebitda],
                      ["Wynik netto", latest?.netProfit, previous?.netProfit],
                      ["Kapitał własny", latest?.equity, previous?.equity],
                      ["Płynność bieżąca", latest?.currentRatio, previous?.currentRatio, "x"],
                      ["Zadłużenie ogólne", latest?.debtRatio, previous?.debtRatio, "%"],
                    ].map(([label, value, previousValue, unit]) => {
                      const change = relativeChange(value, previousValue);
                      const display = unit === "x" ? `${Number(value || 0).toFixed(2)}x` : unit === "%" ? Number(value || 0).toLocaleString("pl-PL", { style: "percent", maximumFractionDigits: 1 }) : formatMoney(value);
                      return <HeroKpi key={label} $negative={change < 0}><span>{label}</span><strong>{display}</strong><small>{formatChange(change)}</small></HeroKpi>;
                    })}
                  </HeroKpis>
                </Hero>

                {activeSection === "overview" && (
                  <Panel>
                    <h2><Building2 size={22} /> Obraz finansowy firmy</h2>
                    <Lead>{narrative(latest, previous)}</Lead>
                    <TwoColumns>
                      <ChartBox><h3>Przychody i koszty operacyjne</h3><LineChart years={years} series={[
                        { name: "Przychody", values: analysis.metrics.map((item) => item.revenue), color: "#2e7d32" },
                        { name: "Koszty", values: analysis.metrics.map((item) => item.operatingCosts), color: "#87ceeb" },
                      ]} /></ChartBox>
                      <ChartBox><h3>Wynik netto i kapitał własny</h3><LineChart years={years} series={[
                        { name: "Wynik netto", values: analysis.metrics.map((item) => item.netProfit), color: "#c62828" },
                        { name: "Kapitał własny", values: analysis.metrics.map((item) => item.equity), color: "#1565c0" },
                      ]} /></ChartBox>
                    </TwoColumns>
                    <h3>Najważniejsze sygnały – {latest?.year}</h3>
                    <MetricCards metrics={[
                      latest?.profitability.find((item) => item.code === "W06"),
                      latest?.liquidity.find((item) => item.code === "W11"),
                      latest?.leverage.find((item) => item.code === "W16"),
                      latest?.efficiency.find((item) => item.code === "W25"),
                    ].filter(Boolean)} />
                  </Panel>
                )}

                {activeSection === "statement" && (
                  <Panel>
                    <h2><FileSearch size={22} /> Sprawozdanie ustrukturyzowane</h2>
                    <Lead>Pozycje odtworzone bezpośrednio ze ścieżek XML. KwotaA tworzy okres porównawczy, a KwotaB okres bieżący.</Lead>
                    <h3>Bilans</h3><StatementTable years={analysis.years} group="balance" />
                    <h3>Rachunek zysków i strat</h3><StatementTable years={analysis.years} group="income" />
                  </Panel>
                )}

                {activeSection === "profitability" && renderMetricSection(
                  "profitability", "Rentowność", TrendingUp,
                  "Marże, EBITDA oraz zwrot z aktywów i kapitału. Wskaźniki na stanach średnich są liczone dopiero wtedy, gdy dostępny jest poprzedni bilans.",
                  [
                    { name: "Marża operacyjna", values: analysis.metrics.map((year) => year.profitability.find((item) => item.code === "W02")?.value), color: "#2e7d32" },
                    { name: "Marża netto", values: analysis.metrics.map((year) => year.profitability.find((item) => item.code === "W06")?.value), color: "#1565c0" },
                  ], true
                )}

                {activeSection === "liquidity" && renderMetricSection(
                  "liquidity", "Płynność", WalletCards,
                  "Zdolność do regulowania zobowiązań krótkoterminowych. Wysoka wartość zwiększa bezpieczeństwo, ale może również oznaczać niewykorzystany kapitał.",
                  [
                    { name: "Płynność bieżąca", values: analysis.metrics.map((year) => year.liquidity.find((item) => item.code === "W11")?.value), color: "#2e7d32" },
                    { name: "Płynność szybka", values: analysis.metrics.map((year) => year.liquidity.find((item) => item.code === "W12")?.value), color: "#87ceeb" },
                  ]
                )}

                {activeSection === "leverage" && renderMetricSection(
                  "leverage", "Zadłużenie i finansowanie", Landmark,
                  "Struktura finansowania, dług oprocentowany, dług netto i zdolność obsługi odsetek.",
                  [
                    { name: "Zadłużenie", values: analysis.metrics.map((year) => year.leverage.find((item) => item.code === "W16")?.value), color: "#c62828" },
                    { name: "Samofinansowanie", values: analysis.metrics.map((year) => year.leverage.find((item) => item.code === "W18")?.value), color: "#2e7d32" },
                  ], true
                )}

                {activeSection === "efficiency" && renderMetricSection(
                  "efficiency", "Sprawność i cykle", Gauge,
                  "Cykle oparte na średnich stanach bilansowych. Cykl należności liczony jest od przychodów netto, ponieważ e-SF nie zawiera sprzedaży brutto z VAT.",
                  [
                    { name: "Należności", values: analysis.metrics.map((year) => year.efficiency.find((item) => item.code === "W25")?.value), color: "#c62828" },
                    { name: "Zobowiązania", values: analysis.metrics.map((year) => year.efficiency.find((item) => item.code === "W28")?.value), color: "#2e7d32" },
                    { name: "Konwersja gotówki", values: analysis.metrics.map((year) => year.efficiency.find((item) => item.code === "W29")?.value), color: "#1565c0" },
                  ]
                )}

                {activeSection === "quality" && (
                  <Panel>
                    <h2><Scale size={22} /> Kontrola spójności danych</h2>
                    <Lead>Automatyczne kontrole bilansu, wyniku netto oraz nakładających się okresów porównawczych. Nowsza wartość porównawcza ma pierwszeństwo, ponieważ stanowi przekształconą podstawę używaną w najnowszym sprawozdaniu; wcześniejsze zamknięcie pozostaje widoczne jako rozbieżność.</Lead>
                    <CheckList>
                      {analysis.quality.checks.map((check) => (
                        <CheckRow key={check.label} $ok={check.ok}>
                          {check.ok ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
                          <div><strong>{check.label}</strong><small>Różnica: {formatMoney(check.difference, 2)}</small></div>
                        </CheckRow>
                      ))}
                      {analysis.quality.overlapIssues.slice(0, 20).map((issue) => (
                        <CheckRow key={`${issue.year}-${issue.path}`} $ok={false}>
                          <AlertTriangle size={20} />
                          <div><strong>Rozbieżność danych porównawczych {issue.year}</strong><small>{issue.path} · różnica {formatMoney(issue.difference, 2)}</small></div>
                        </CheckRow>
                      ))}
                      {analysis.quality.overlapIssues.length > 20 && (
                        <CheckRow $ok={false}>
                          <AlertTriangle size={20} />
                          <div><strong>Pozostałe rozbieżności: {analysis.quality.overlapIssues.length - 20}</strong><small>Lista została skrócona do 20 pozycji, aby zachować czytelność raportu.</small></div>
                        </CheckRow>
                      )}
                    </CheckList>
                    <h3>Pliki źródłowe</h3>
                    <TableScroll>
                      <table><thead><tr><th>Plik</th><th>Okres</th><th>Schema</th><th>Pozycje</th></tr></thead><tbody>
                        {details.map((document) => <tr key={document._id}><td>{document.fileName}</td><td>{document.metadata?.periodFrom} – {document.metadata?.periodTo}</td><td>{document.metadata?.systemCode} / {document.metadata?.schemaVersion}</td><td>{document.summary?.financialRowCount}</td></tr>)}
                      </tbody></table>
                    </TableScroll>
                    <Lead style={{ marginTop: 16 }}>Metodyka {METHODOLOGY_VERSION}. Obliczenia zachowują pełną precyzję; zaokrąglenie następuje wyłącznie podczas wyświetlania. Dzielenie przez zero zwraca „n/d”, nigdy nieskończoność.</Lead>
                  </Panel>
                )}
              </>
            )}
          </Report>
        </AnalysisBody>
      </Content>
    </ImportWrapper>
  );
}
